/**
 * ResumeCard — upload (drop zone + file picker), replace, download, delete
 * (confirmed), parse status and "Re-parse". Download goes through the bearer
 * and a blob URL because the iframe cannot put headers on a plain link.
 */
import { useId, useRef, useState } from 'react';
import { Download, FileText, Loader2, RefreshCw, Trash2, Upload } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { errorMessage, useToast } from '@/components/Toast';
import { fullDateTime, relativeTime } from '@/lib/time';
import type { ResumeSummary } from '@/features/pipeline/types';
import { RESUME_ACCEPT, resumeFileProblem, useLeadProfileMutations } from './lead-api';
import { formatBytes, parseStatusMeta } from './resume-format';

const actionClass =
  'inline-flex min-h-10 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-navy hover:bg-page disabled:opacity-50';

export function ResumeCard({ leadId, resume }: { leadId: string; resume: ResumeSummary | null }) {
  const { upload, remove, reparse, download } = useLeadProfileMutations(leadId);
  const toast = useToast();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pendingName, setPendingName] = useState('');

  const pick = (file: File | undefined) => {
    if (!file) return;
    const bad = resumeFileProblem(file);
    setProblem(bad);
    if (bad) return;
    setPendingName(file.name);
    upload.mutate(file, {
      onSuccess: () => toast.push({ kind: 'success', title: resume ? 'Resume replaced' : 'Resume uploaded' }),
      onError: (err) => setProblem(errorMessage(err, 'Upload failed. Try again.')),
    });
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    pick(e.dataTransfer.files[0]);
  };

  const status = resume ? parseStatusMeta(resume.parse_status, resume.parse_error) : null;
  const busy = upload.isPending;

  return (
    <section aria-labelledby={`${inputId}-h`} className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-2 px-3 pt-2.5">
        <h3 id={`${inputId}-h`} className="text-[13px] font-semibold text-gray-900">
          Resume
        </h3>
        {status && !busy && (
          <span className={`rounded-full px-2 py-0.5 text-[12px] font-medium ${status.chip}`} data-testid="parse-status">
            {status.label}
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={RESUME_ACCEPT}
        className="sr-only"
        aria-label={resume ? 'Replace resume file' : 'Choose resume file'}
        onChange={(e) => pick(e.target.files?.[0])}
        disabled={busy}
      />

      {busy ? (
        <div className="mx-3 my-3 rounded-md border border-navy/30 bg-page px-3 py-3" role="status" aria-busy="true">
          <div className="flex items-center gap-2 text-[13px] text-gray-900">
            <Loader2 size={15} strokeWidth={2} className="animate-spin text-navy" aria-hidden />
            Uploading {pendingName}…
          </div>
          <div className="mt-2 h-1 overflow-hidden rounded bg-gray-200">
            <div className="h-full w-1/3 animate-pulse rounded bg-navy" />
          </div>
        </div>
      ) : resume ? (
        <div className="px-3 pb-3 pt-2">
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-navy/10 text-navy">
              <FileText size={18} strokeWidth={2} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium text-gray-900" title={resume.filename ?? undefined}>
                {resume.filename ?? 'Resume'}
              </div>
              <div className="text-[12px] text-muted">
                {[formatBytes(resume.size), resume.uploaded_at && `uploaded ${relativeTime(resume.uploaded_at)}`]
                  .filter(Boolean)
                  .join(' · ')}
                {resume.parsed_at && resume.parse_status === 'parsed' && (
                  <>
                    <span aria-hidden> · </span>
                    <time dateTime={resume.parsed_at} title={fullDateTime(resume.parsed_at)}>
                      parsed {relativeTime(resume.parsed_at)}
                    </time>
                  </>
                )}
              </div>
            </div>
          </div>
          {status?.hint && (
            <p className={`mt-2 text-[12px] ${resume.parse_status === 'failed' ? 'text-danger' : 'text-muted'}`}>
              {status.hint}
            </p>
          )}
          <div className="-mx-1 mt-2 flex flex-wrap">
            <button
              type="button"
              className={actionClass}
              disabled={download.isPending}
              onClick={() =>
                download.mutate(resume.filename ?? 'resume', {
                  onError: (err) => toast.push({ kind: 'error', title: 'Download failed', message: errorMessage(err) }),
                })
              }
            >
              <Download size={14} strokeWidth={2} aria-hidden /> {download.isPending ? 'Preparing…' : 'Download'}
            </button>
            <button type="button" className={actionClass} onClick={() => inputRef.current?.click()}>
              <Upload size={14} strokeWidth={2} aria-hidden /> Replace
            </button>
            <button
              type="button"
              className={actionClass}
              disabled={reparse.isPending || resume.parse_status === 'pending'}
              onClick={() =>
                reparse.mutate(undefined, {
                  onError: (err) => toast.push({ kind: 'error', title: 'Could not start parsing', message: errorMessage(err) }),
                })
              }
            >
              <RefreshCw size={14} strokeWidth={2} aria-hidden /> Re-parse
            </button>
            <button type="button" className={`${actionClass} text-danger hover:bg-danger-bg`} onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} strokeWidth={2} aria-hidden /> Delete
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="resume-dropzone"
          className={`mx-3 my-3 rounded-md border border-dashed px-3 py-4 text-center ${
            dragging ? 'border-navy bg-navy/5' : 'border-gray-300'
          }`}
        >
          <Upload size={18} strokeWidth={2} className="mx-auto text-gray-500" aria-hidden />
          <p className="mt-1.5 text-[13px] text-gray-900">
            Drop a resume here, or{' '}
            <label htmlFor={inputId} className="cursor-pointer font-medium text-navy underline underline-offset-2">
              choose a file
            </label>
          </p>
          <p className="mt-0.5 text-[12px] text-muted">PDF or Word, up to 10 MB. Work history is extracted automatically.</p>
        </div>
      )}
      {problem && (
        <p role="alert" className="px-3 pb-3 text-[12px] text-danger">
          {problem}
        </p>
      )}

      <Modal
        open={confirmDelete}
        title="Delete this resume?"
        description="The file and the extracted work history are removed from this candidate."
        onClose={() => setConfirmDelete(false)}
        footer={
          <>
            <button type="button" onClick={() => setConfirmDelete(false)} className="inline-flex min-h-10 items-center justify-center rounded-md border border-gray-200 bg-white px-3.5 text-sm font-medium text-gray-800 hover:bg-page">
              Keep resume
            </button>
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(undefined, {
                  onSuccess: () => { setConfirmDelete(false); toast.push({ kind: 'success', title: 'Resume deleted' }); },
                  onError: (err) => toast.push({ kind: 'error', title: 'Could not delete resume', message: errorMessage(err) }),
                })
              }
              className="inline-flex min-h-10 items-center justify-center rounded-md bg-danger px-3.5 text-sm font-medium text-white hover:bg-danger-dark disabled:opacity-50"
            >
              {remove.isPending ? 'Deleting…' : 'Delete resume'}
            </button>
          </>
        }
      />
    </section>
  );
}
